import {
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import {useSearchParams} from 'react-router-dom';
import {useEffect, useRef, useState} from 'react';
import {db} from '../firebase_setup/firebase';
import {ref, get} from 'firebase/database';

export const LeaderBoard = () => {
  const [searchParams] = useSearchParams();
  const currentPlayerEmail = searchParams.get('email');

  const [leaderboardData, setLeaderboardData] = useState([]);
  const [currentPlayerRank, setCurrentPlayerRank] = useState(0);
  const [isScoreFixed, setIsScoreFixed] = useState(false); // Track if player's score is fixed at the bottom
  const tableRef = useRef(null);

  const [scrollDirection, setScrollDirection] = useState(''); // Track scroll direction

  useEffect(() => {
    const fetchData = async () => {
      const dbRef = ref(db, 'uv-astro-leaderboard');
      const snapshot = await get(dbRef);
      if (snapshot.exists()) {
        const data = Object.values(snapshot.val());
        // Sort players by score in descending order
        const sortedData = data.sort((a, b) => b.score - a.score);

        // Assign ranks and find current player
        let rank = 1;
        let currentPlayerIndex = -1;
        const rankedData = sortedData.map((player, index) => {
          if (player.playerEmail === currentPlayerEmail) {
            currentPlayerIndex = index;
          }
          return {...player, rank: rank++};
        });

        setLeaderboardData(rankedData);
        setCurrentPlayerRank(currentPlayerIndex + 1); // Save the user's rank
      }
    };

    fetchData();
  }, [currentPlayerEmail]);

  const handleScroll = () => {
    const table = tableRef.current;
    if (table) {
      const tableRect = table.getBoundingClientRect();
      const currentPlayerRow = document.getElementById(
        `player-row-${currentPlayerRank}`,
      );
      if (currentPlayerRow) {
        const playerRowRect = currentPlayerRow.getBoundingClientRect();
        // If the player's rank is not visible, show the fixed row at the bottom
        if (
          playerRowRect.bottom < tableRect.top ||
          playerRowRect.top > tableRect.bottom
        ) {
          setIsScoreFixed(true); // Show the fixed row at the bottom
        } else {
          setIsScoreFixed(false); // Hide the fixed row if player's rank is visible
        }
      }
    }
  };

  const getDisplayedPlayers = () => {
    if (leaderboardData.length === 0) return [];
    const totalPlayers = leaderboardData.length;
    const topPlayers = leaderboardData.slice(0, 9);

    const halfWindow = 4;
    let startIndex = Math.max(currentPlayerRank - halfWindow, 0);
    let endIndex = Math.min(currentPlayerRank + halfWindow + 1, totalPlayers);

    const playerSlice = leaderboardData.slice(startIndex, endIndex);

    const mergedPlayers = [
      ...new Map(
        [...topPlayers, ...playerSlice].map(player => [player.rank, player]),
      ).values(),
    ];

    mergedPlayers.sort((a, b) => a.rank - b.rank);

    const allPlayers = leaderboardData.filter(
      player => !mergedPlayers.some(merged => merged.rank === player.rank),
    );

    const finalResult = [...mergedPlayers, ...allPlayers].sort(
      (a, b) => a.rank - b.rank,
    );

    return finalResult;
  };

  // Scroll to the user's rank when the page loads
  useEffect(() => {
    const currentPlayerRow = document.getElementById(
      `player-row-${currentPlayerRank}`,
    );
    if (currentPlayerRow) {
      // Scroll the table to center the user's rank
      currentPlayerRow.scrollIntoView({
        behavior: 'smooth',
        block: 'center', // Ensure the user's rank is centered vertically
      });
    }
  }, [currentPlayerRank]);

  return (
    <>
      <Grid
        container
        justifyContent="space-evenly"
        fontFamily="Klapt"
        marginTop="20px"
        color="#DEE0E1"
      >
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <ArrowRightIcon
              style={{
                fontSize: '40px',
                color: '#D9D9D9',
                filter: 'drop-shadow(0 0 5px white)',
                height: '30px',
                marginTop: '4px',
              }}
            />
            <Typography
              variant="body1"
              className="animated"
              style={{
                fontWeight: '700',
                fontSize: '19px',
                textAlign: 'center',
                fontFamily: 'Klapt',
                letterSpacing: '1px',
                margin: '0px',
              }}
            >
              LEADERBOARD
            </Typography>
            <ArrowLeftIcon
              style={{
                fontSize: '40px',
                color: '#D9D9D9',
                filter: 'drop-shadow(0 0 5px white)',
                height: '30px',
                marginTop: '4px',
              }}
            />
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{height: '55px'}}
          mt={1}
        >
          <div className="plus-sign">
            <div className="line horizontal"></div>
            <div className="line vertical"></div>
          </div>
        </Grid>

        <Grid item xs={12}>
          <Box>
            <Table style={{tableLayout: 'fixed'}}>
              <TableHead
                style={{
                  backgroundColor: 'transparent',
                  color: '#DEE0E1',
                  border: 'none',
                }}
              >
                <TableRow>
                  <TableCell
                    style={{
                      color: '#DEE0E1',
                      border: 'none',
                      fontWeight: '700',
                      fontFamily: 'klapt',
                      textAlign: 'center',
                      fontSize: '18px',
                      padding: '10px 15px',
                    }}
                  >
                    RANK
                  </TableCell>
                  <TableCell
                    style={{
                      color: '#DEE0E1',
                      border: 'none',
                      fontWeight: '700',
                      fontFamily: 'klapt',
                      textAlign: 'center',
                      fontSize: '18px',
                      padding: '10px 15px',
                    }}
                  >
                    NAME
                  </TableCell>
                  <TableCell
                    style={{
                      color: '#DEE0E1',
                      border: 'none',
                      fontWeight: '700',
                      fontFamily: 'klapt',
                      textAlign: 'center',
                      fontSize: '18px',
                      padding: '10px 15px',
                    }}
                  >
                    POINTS
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </Box>
          <Box
            ref={tableRef}
            onScroll={handleScroll}
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              scrollBehavior: 'smooth',
              position: 'relative', // Added to help contain the scroll behavior
            }}
          >
            <Table style={{tableLayout: 'fixed'}}>
              <TableBody
                style={{
                  backgroundImage: `url("${process.env.REACT_APP_CDN}/tableBck.png")`,
                }}
              >
                {getDisplayedPlayers().map(player => (
                  <TableRow
                    key={player.playerEmail}
                    id={`player-row-${player.rank}`}
                    style={{
                      backgroundImage:
                        player.playerEmail === currentPlayerEmail
                          ? `url("${process.env.REACT_APP_CDN}/selected.png")`
                          : 'none',
                      backgroundColor: 'transparent', // Highlight current player
                      border: 'none',
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                    }}
                  >
                    <TableCell
                      style={{
                        border: 'none',
                        color: '#fff',
                        textAlign: 'center',
                        fontFamily: 'DisketMono',
                        fontSize: '16px',
                      }}
                    >
                      {String(player.rank).padStart(2, '0')}
                    </TableCell>

                    <TableCell
                      style={{
                        border: 'none',
                        color: '#fff',
                        textAlign: 'center',
                        fontFamily: 'DisketMono',
                        fontSize: '16px',
                      }}
                    >
                      {player.playerName}
                    </TableCell>
                    <TableCell
                      style={{
                        border: 'none',
                        color: '#fff',
                        textAlign: 'center',
                        fontFamily: 'DisketMono',
                        fontSize: '16px',
                      }}
                    >
                      {player.score}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {isScoreFixed && (
            <Box
              style={{
                bottom: '0',
                left: '0',
                width: '100%',
                textAlign: 'center',
                zIndex: '10',
                visibility: isScoreFixed ? 'visible' : 'hidden',
                backgroundImage: `url("${process.env.REACT_APP_CDN}/selected.png")`,
                backgroundColor: 'transparent', // Highlight current player
                border: 'none',
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            >
              <Table style={{tableLayout: 'fixed'}}>
                <TableRow>
                  <TableCell
                    style={{
                      border: 'none',
                      color: '#fff',
                      textAlign: 'center',
                      fontFamily: 'DisketMono',
                      fontSize: '16px',
                    }}
                  >
                    {String(
                      leaderboardData[currentPlayerRank - 1]?.rank,
                    ).padStart(2, '0')}
                  </TableCell>

                  <TableCell
                    style={{
                      border: 'none',
                      color: '#fff',
                      textAlign: 'center',
                      fontFamily: 'DisketMono',
                      fontSize: '16px',
                    }}
                  >
                    {leaderboardData[currentPlayerRank - 1]?.playerName}
                  </TableCell>
                  <TableCell
                    style={{
                      border: 'none',
                      color: '#fff',
                      textAlign: 'center',
                      fontFamily: 'DisketMono',
                      fontSize: '16px',
                    }}
                  >
                    {leaderboardData[currentPlayerRank - 1]?.score}
                  </TableCell>
                </TableRow>
              </Table>
            </Box>
          )}
        </Grid>

        <Grid
          item
          xs={12}
          style={{
            position: 'relative',
            width: '89%',
          }}
        >
          <Button
            sx={{
              borderLeft: '1px solid #FFFFFF',
              borderRight: '1px solid #FFFFFF',
              borderBottom: '1px solid #FFFFFF',
              backgroundColor: '#000000',
              color: 'white',
              position: 'relative',
              marginTop: '45px',
              padding: '10px',
              width: '100%',
              height: '56px',
              fontSize: '16px',
              borderRadius: 0,
              marginBottom: '20px',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '25%',
                borderTop: '1px solid #FFFFFF',
              },
              '&:hover': {
                backgroundColor: 'transparent',
              },
              fontFamily: 'DisketMono',
            }}
            onClick={() => {
              window.location.href = 'https://uvastro.testserver2.co.in/';
            }}
          >
            RETRY
          </Button>
        </Grid>
      </Grid>
    </>
  );
};
